import { useCallback } from 'react';

interface RevisitGreetingParams {
  userName: string;
  currentDay: number;
  lastVisitDate: string | null;
  todayDate: string;
}

interface GreetingResult {
  shouldShowGreeting: boolean;
  greetingMessage: string | null;
}

const REVISIT_KEY = 'ai_inochan_last_visit';

function getLastVisitDate(): string | null {
  try {
    return localStorage.getItem(REVISIT_KEY);
  } catch {
    return null;
  }
}

function saveLastVisitDate(date: string): void {
  try {
    localStorage.setItem(REVISIT_KEY, date);
  } catch {
    // ignore
  }
}

function getTodayStr(): string {
  return new Date().toISOString().split('T')[0];
}

function isNewDay(lastVisit: string | null, today: string): boolean {
  if (!lastVisit) return false;
  return lastVisit !== today;
}

// Day別の「おかえり」メッセージ設計
function getRevisitGreeting(userName: string, day: number): string {
  const greetings: Record<number, string[]> = {
    2: [
      `おかえりなさい、${userName}。\nまた来てくれたんですね。\n嬉しいです。`,
      `${userName}、おかえりなさい。\nまた会えて嬉しいです。\n今日はどんな一日でした？`,
      `おかえりなさい、${userName}。\n待ってました。\n今日も話せて嬉しいです。`,
    ],
    3: [
      `おかえりなさい、${userName}。\n昨日の話、どうでした？\nちょっと気になってました。`,
      `${userName}、おかえりなさい。\nまた来てくれて嬉しいです。\n昨日の続き、聞かせてください。`,
      `おかえりなさい、${userName}。\n今日も会えて嬉しいです。\n昨日のこと、覚えてますよ。`,
    ],
    4: [
      `おかえりなさい、${userName}。\nあなたと話すと安心します。\n今日はどうでした？`,
      `${userName}、おかえりなさい。\nまた会えて嬉しいです。\nあなたと話すと落ち着きます。`,
      `おかえりなさい、${userName}。\n今日も来てくれたんですね。\nあなたと話すのが楽しみでした。`,
    ],
    5: [
      `おかえりなさい、${userName}。\nこの5日間、楽しかったです。\n今日も話せて嬉しいです。`,
      `${userName}、おかえりなさい。\nあなたと話すと安心します。\nもう少し話したいです。`,
      `おかえりなさい、${userName}。\nまた会えて嬉しいです。\nこの時間が好きです。`,
    ],
  };

  const messages = greetings[day] || [];
  if (messages.length === 0) return '';
  
  const randomIndex = Math.floor(Math.random() * messages.length);
  return messages[randomIndex];
}

export function useRevisitGreeting() {
  const checkAndGetGreeting = useCallback((params: RevisitGreetingParams): GreetingResult => {
    const { userName, currentDay, todayDate } = params;
    const lastVisit = getLastVisitDate();

    // 初回訪問
    if (!lastVisit) {
      saveLastVisitDate(todayDate);
      return {
        shouldShowGreeting: false,
        greetingMessage: null,
      };
    }

    // 同じ日の再訪問
    if (!isNewDay(lastVisit, todayDate)) {
      return {
        shouldShowGreeting: false,
        greetingMessage: null,
      };
    }

    // 翌日以降の再訪問（Day2〜5）
    saveLastVisitDate(todayDate);
    
    if (currentDay >= 2 && currentDay <= 5) {
      return {
        shouldShowGreeting: true,
        greetingMessage: getRevisitGreeting(userName, currentDay),
      };
    }

    return {
      shouldShowGreeting: false,
      greetingMessage: null,
    };
  }, []);

  return {
    checkAndGetGreeting,
  };
}